import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import config from '../config';
import {
    History,
    MapPin,
    Loader2,
    AlertCircle,
    TrendingUp,
    Calculator
} from 'lucide-react';

export default function MyConsumption() {
    const { selectedProperty } = useOutletContext();
    const navigate = useNavigate();
    const [consumptionData, setConsumptionData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchConsumption = async () => {
        if (!selectedProperty?.nro_cliente) return;

        const token = sessionStorage.getItem('token');
        if (!token) {
            // Mock data con cálculo de años reales y campo ano_consumo
            const mockConsumption = Array.from({ length: 13 }, (_, i) => {
                const date = new Date();
                date.setMonth(date.getMonth() - i);
                const meses = ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO", "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"];
                const lecturaBase = 2000 + ((13 - i) * 18);
                return {
                    desc_mes: meses[date.getMonth()],
                    ano_consumo: date.getFullYear(),
                    consumo_mes: Math.floor(Math.random() * 10) + 12,
                    lectura_actual: lecturaBase + 15,
                    lectura_anterior: lecturaBase,
                    desc_estado_lectura: "LEÍDO"
                };
            });
            setConsumptionData(mockConsumption);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                nro_cliente: selectedProperty.nro_cliente
            });

            const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-readings?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 401) {
                sessionStorage.clear();
                navigate('/login');
                return;
            }

            if (!response.ok) throw new Error(`Error ${response.status}`);

            const data = await response.json();
            setConsumptionData(Array.isArray(data) ? data : []);
        } catch (err) {
            setError("No se pudo cargar el historial de consumos.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConsumption();
    }, [selectedProperty]);

    const last3Months = consumptionData.slice(0, 3);
    const avg3Months = last3Months.length > 0
        ? (last3Months.reduce((acc, curr) => acc + curr.consumo_mes, 0) / last3Months.length).toFixed(1)
        : 0;

    const maxConsumo = (consumptionData || []).length > 0
        ? Math.max(...consumptionData.map(d => d.consumo_mes))
        : 1;

    const chartData = [...(consumptionData || [])].slice(0, 13).reverse();

    return (
        <div className="max-w-4xl mx-auto w-full animate-in fade-in duration-500">
            <div className="mb-8 lg:mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="text-3xl lg:text-4xl font-black text-slate-800 mb-2">Mis Consumos</h1>
                    <p className="text-slate-500 font-medium">Información detallada de lecturas y uso de agua.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 md:col-span-2 flex items-center gap-4">
                    <div className="p-3 bg-sky-100 text-sky-600 rounded-2xl">
                        <MapPin size={24} />
                    </div>
                    <div>
                        <label className="block text-slate-400 text-[10px] font-black uppercase tracking-wider mb-0.5">Servicio Seleccionado</label>
                        <p className="text-slate-700 font-bold text-lg leading-tight">
                            #{selectedProperty?.nro_cliente} - {selectedProperty?.up_direccion}
                        </p>
                    </div>
                </div>

                <div className="bg-sky-600 p-6 rounded-3xl shadow-lg shadow-sky-100 text-white flex items-center gap-4">
                    <div className="p-3 bg-white/20 rounded-2xl">
                        <Calculator size={24} />
                    </div>
                    <div>
                        <label className="block text-sky-200 text-[10px] font-black uppercase tracking-wider mb-0.5">Promedio últimos 3 meses</label>
                        <p className="text-2xl font-black">{avg3Months} <span className="text-sm font-bold opacity-80">{config.unitMeasure}</span></p>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 text-sky-600 no-print">
                    <Loader2 className="animate-spin mb-4" size={40} />
                    <p className="font-bold">Analizando historial...</p>
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-100 p-6 rounded-2xl flex items-center gap-4 text-red-700 mb-6 no-print">
                    <AlertCircle size={24} />
                    <p className="font-medium flex-1">{error}</p>
                    <button onClick={fetchConsumption} className="px-6 py-2 bg-red-100 hover:bg-red-200 rounded-xl font-bold transition-all active:scale-95">
                        Reintentar
                    </button>
                </div>
            ) : (
                <>
                    <div className="bg-white p-6 lg:p-8 rounded-3xl shadow-sm border border-slate-100 mb-6 no-print">
                        <div className="flex items-center justify-between mb-12">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-sky-50 text-sky-600 rounded-lg"><TrendingUp size={20} /></div>
                                <h3 className="text-lg font-bold text-slate-800">Evolución (13 Meses)</h3>
                            </div>
                            <span className="text-xs font-black text-slate-400 uppercase tracking-widest bg-slate-50 px-3 py-1 rounded-full">
                                Unidad: {config.unitMeasure}
                            </span>
                        </div>

                        <div className="relative h-72 w-full flex items-end justify-between gap-1 md:gap-3 pb-4 border-b border-slate-100">
                            {chartData.map((consumo, idx) => {
                                const isComparison = idx === 0 || idx === chartData.length - 1;
                                const shortYear = consumo.ano_consumo ? `'${consumo.ano_consumo.toString().slice(-2)}` : '';

                                return (
                                    <div key={idx} className="flex-1 flex flex-col justify-end items-center group relative h-full">
                                        {/* Valor y Unidad siempre visibles */}
                                        <div className={`mb-2 text-[8px] md:text-[10px] font-black px-1.5 py-0.5 rounded-md absolute -top-8 z-10 whitespace-nowrap transition-all
                                            ${isComparison ? 'bg-sky-600 text-white shadow-md scale-110' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-800 group-hover:text-white'}`}>
                                            {consumo.consumo_mes}<span className="ml-0.5 opacity-70">{config.unitMeasure}</span>
                                        </div>

                                        <div
                                            style={{ height: `${(consumo.consumo_mes / maxConsumo) * 100}%` }}
                                            className={`w-full max-w-[32px] transition-all rounded-t-lg shadow-sm
                                                ${isComparison ? 'bg-sky-600 ring-4 ring-sky-50' : 'bg-sky-200 group-hover:bg-sky-300'}`}
                                        />

                                        <div className="flex flex-col items-center mt-4 gap-0.5">
                                            <span className={`text-[8px] md:text-[9px] font-black uppercase truncate w-full text-center
                                                ${isComparison ? 'text-sky-700' : 'text-slate-400'}`}>
                                                {consumo.desc_mes.substring(0, 3)}
                                            </span>
                                            <span className="text-[7px] md:text-[8px] font-bold text-slate-300">
                                                {shortYear}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden mb-10">
                        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-slate-100 text-slate-600 rounded-lg"><History size={20} /></div>
                                <h3 className="text-lg font-bold text-slate-800">Detalle de Lecturas</h3>
                            </div>
                        </div>
                        <div className="divide-y divide-slate-50">
                            {consumptionData.map((consumo, idx) => (
                                <div key={idx} className="reading-row flex flex-col md:flex-row md:justify-between md:items-center p-6 hover:bg-slate-50/50 transition-colors gap-6">
                                    <div className="flex flex-col min-w-[140px]">
                                        <div className="flex items-center gap-1.5 mb-1">
                                            <span className="font-black text-slate-800 text-xl capitalize leading-none">
                                                {consumo.desc_mes.toLowerCase()}
                                            </span>
                                            <span className="font-bold text-slate-400 text-sm">
                                                {consumo.ano_consumo}
                                            </span>
                                        </div>
                                        <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full w-fit uppercase border border-emerald-100">
                                            {consumo.desc_estado_lectura}
                                        </span>
                                    </div>

                                    <div className="flex-1 grid grid-cols-2 gap-4">
                                        <div className="bg-slate-50/80 p-3 rounded-2xl border border-slate-100">
                                            <label className="block text-[9px] font-black text-slate-400 uppercase mb-1">Lectura Anterior</label>
                                            <p className="text-slate-700 font-bold font-mono tracking-tighter text-lg">{consumo.lectura_anterior}</p>
                                        </div>
                                        <div className="bg-sky-50/50 p-3 rounded-2xl border border-sky-100">
                                            <label className="block text-[9px] font-black text-sky-500 uppercase mb-1">Lectura Actual</label>
                                            <p className="text-sky-700 font-bold font-mono tracking-tighter text-lg">{consumo.lectura_actual}</p>
                                        </div>
                                    </div>

                                    <div className="text-right md:min-w-[100px]">
                                        <label className="block text-[9px] font-black text-slate-400 uppercase mb-1">Consumo Total</label>
                                        <span className="block font-black text-slate-800 text-2xl tracking-tighter">
                                            {consumo.consumo_mes} <span className="text-xs text-slate-300 font-bold">{config.unitMeasure}</span>
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
